import { useEffect, useRef } from "react";
import cn from "classnames";
import { createAirUpdateCardHook, useAirCardActions } from "features/AirCardActions";
import { cardToDeleteStore } from "features/CardToDelete";
import { filteredCardsStore } from "features/Filter";
import { useFilterContext } from "features/Filter/ui/context/Filter.context";
import { LoadMoreButton } from "features/LoadMoreButton";
import { openedCardStore } from "features/OpenedCard";
import { useCardHandlers } from "features/Panel";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { tagsStore, useTagsContext } from "features/Tags";
import {
  AIR_FILTERED_CARDS_QUERY_CACHE_KEY,
  AirSavedCard,
  isAirSavedCardModel,
  useAirInfiniteScrollFetch,
} from "entities/AirSavedCard";
import { authStore } from "entities/Auth";
import { Card, LoadCard } from "entities/Card";
import { PanelView } from "entities/PanelView";
import { searchFilterStore } from "entities/SearchFilter";
import { CARDS_REQUEST_LIMIT } from "shared/constants/common.constants";
import { savedShipmentLoadingText } from "shared/constants/panel.constants";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
import s from "../FilteredCardsView.module.scss";

type AirFilteredCardsViewProps = {
  className?: string;
};

export const AirFilteredCardsView: RFC<AirFilteredCardsViewProps> = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const containerRef = useRef(null);
  const { filter, filterChanged, isAirFilter } = useFilterContext();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const filteredCards = useAppSelector(filteredCardsStore.selectors.filteredCards);
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const shipmentType = useAppSelector(searchFilterStore.selectors.shipment);
  const { setFilteredCardsViewHistory, currentHistoryState } = usePanelViews();
  const { openCard } = useCardHandlers({
    openedCardAction: openedCardStore.actions.setOpenedCardAction,
  });
  const { refreshTags } = useTagsContext();
  const { deleteAirCardHandler, isAirCardDeleting } = useAirCardActions({
    setCardToDeleteAction: (card) => ({
      type: cardToDeleteStore.actions.setCardToDeleteAction.type,
      payload: card,
    }),
    resetCardToDeleteAction: () => ({
      type: cardToDeleteStore.actions.resetCardToDeleteAction.type,
    }),
    removeFilteredCardAction: (card) => ({
      type: filteredCardsStore.actions.removeFilteredCardAction.type,
      payload: card,
    }),
    removeCardByTagAction: (card) => ({
      type: tagsStore.actions.removeCardByTagAction.type,
      payload: card,
    }),
    refreshTags,
  });

  const airFilteredCards = filteredCards.filter((card) => isAirSavedCardModel(card));

  const {
    requestMoreCards,
    setRequestFilterSync: setRequestFilter,
    shouldRenderAirMoreButton,
    shouldRenderAirInfiniteQueryLoader,
    cancelGetCardsRequest,
    resetAirCardsQuery: resetCardsQuery,
  } = useAirInfiniteScrollFetch<HTMLDivElement>({
    containerRef,
    onBeforeRequest: () => {
      if (isAirFilter?.(filter)) setRequestFilter({ where: filter.where, order: filter.order });
    },
    onSuccess: ({ page }) => {
      dispatch(filteredCardsStore.actions.appendFilteredCardsAction({ data: page }));
    },
    onSettled: ({ totalCount }) => {
      setFilteredCardsViewHistory({ ...filter, totalCount });
    },
    shouldRequestOnMount: () => {
      if (!isAuthenticated) return false;

      const baseCondition = airFilteredCards.length < CARDS_REQUEST_LIMIT;

      if (currentHistoryState?.totalCount) {
        return baseCondition && currentHistoryState.totalCount > CARDS_REQUEST_LIMIT;
      }

      return baseCondition;
    },
    queryKey: [AIR_FILTERED_CARDS_QUERY_CACHE_KEY],
    initialFilter: currentHistoryState?.filter,
  });

  useEffect(() => {
    if (filterChanged()) {
      resetCardsQuery();
      dispatch(filteredCardsStore.actions.clearFilteredCardsAction());
    }
  }, [filter]);

  useEffect(
    () => () => {
      resetCardsQuery();
      cancelGetCardsRequest();
    },
    []
  );

  if (!airFilteredCards.length && !shouldRenderAirInfiniteQueryLoader) {
    return (
      <PanelView ref={containerRef} className={cn(s.containerFilterEmpty, className)} shouldRenderMoreButton={false}>
        <div className={s.emptyContentWrapper}>
          <div className={s.wrapperFilterEmpty}>
            <h3 className={s.emptyTitle}>
              {t("No results for the selected parameter. Please change the filter options")}.
            </h3>
          </div>
        </div>
      </PanelView>
    );
  }

  return (
    <PanelView
      ref={containerRef}
      className={className}
      shouldRenderMoreButton={shouldRenderAirMoreButton}
      loadMoreButton={<LoadMoreButton onClick={requestMoreCards} />}
    >
      {airFilteredCards.map((card) => (
        <AirSavedCard
          key={card.reference}
          data={card}
          openCard={openCard}
          isSavedCardDeleting={isAirCardDeleting}
          useSavedCardUpdateActions={createAirUpdateCardHook({
            model: card,
            isOpenedCardExist: !!openedCard,
            openedCardUpdateAction: (response) => ({
              type: openedCardStore.actions.updateOpenCardAction.type,
              payload: response,
            }),
            updateFilteredCards: (response) => ({
              type: filteredCardsStore.actions.updateFilterCardAction.type,
              payload: response,
            }),
          })}
          deleteCardHandler={deleteAirCardHandler}
          shipmentType={shipmentType}
          renderCard={(props) => <Card {...props} />}
        />
      ))}

      {shouldRenderAirInfiniteQueryLoader && <LoadCard text={t(savedShipmentLoadingText)} />}
    </PanelView>
  );
};
